import React, { useMemo } from "react";
import { geoPath } from "d3-geo";
import { useWorldFeatures } from "@/lib/useWorldFeatures";
import { NUMERIC_TO_ALPHA } from "@/lib/countryColors";
import { isArab, featureKey } from "@/lib/mapGeometry";
import { VIEW_W, VIEW_H, Sea } from "@/components/map/MapParts";
import { makeProjection } from "@/components/map/useAtlasGeometry";
import { REGION_OF } from "@/lib/introContent";

const SMALL = ["BH", "QA", "KW", "DJ", "KM", "PS"];

/** Static map of the Arab world coloured by macro-region; `active` dims the other regions. */
export default function RegionsMap({ countries, active, onRegion, onCountry }) {
  const features = useWorldFeatures();
  const geo = useMemo(() => {
    if (!features) return null;
    const proj = makeProjection();
    const gen = geoPath(proj);
    const pins = countries.filter((c) => SMALL.includes(c.code)).map((c) => ({ code: c.code, xy: proj([c.position.lng, c.position.lat]) }));
    return { gen, arab: features.filter(isArab).map((f) => ({ code: NUMERIC_TO_ALPHA[f.properties.id], d: gen(f) })), others: features.filter((f) => !isArab(f)), pins };
  }, [features, countries]);

  if (!geo) return <div className="aspect-[820/550] rounded-2xl bg-sandDeep/30 animate-pulse" data-testid="regions-map-loading" />;
  const opacity = (code) => (!active || REGION_OF[code]?.id === active ? 1 : 0.25);
  const fill = (code) => REGION_OF[code]?.color || "#999";

  return (
    <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="w-full h-auto rounded-2xl border border-stoneLine shadow-sm" role="img" data-testid="regions-map">
      <defs>
        <radialGradient id="sea-grad" cx="50%" cy="50%" r="75%"><stop offset="0%" stopColor="#E4EDF3" /><stop offset="100%" stopColor="#CBD9E2" /></radialGradient>
      </defs>
      <Sea />
      <g fill="#EDE6D6" stroke="#D6CDB8" strokeWidth="0.5">
        {geo.others.map((f) => <path key={featureKey("o", f)} d={geo.gen(f)} />)}
      </g>
      <g stroke="#FFFDF7" strokeWidth="0.8" strokeLinejoin="round">
        {geo.arab.map((s) => (
          <path key={s.code} d={s.d} fill={fill(s.code)} opacity={opacity(s.code)} className="cursor-pointer transition-opacity duration-300 hover:brightness-110"
            onMouseEnter={() => onRegion?.(REGION_OF[s.code]?.id)} onClick={() => onCountry?.(s.code)} data-testid={`regions-map-${s.code}`}>
            <title>{countries.find((c) => c.code === s.code)?.short_names?.en || s.code}</title>
          </path>
        ))}
      </g>
      <g>
        {geo.pins.map((p) => (
          <circle key={p.code} cx={p.xy[0]} cy={p.xy[1]} r="5" fill={fill(p.code)} stroke="#FFFDF7" strokeWidth="1.5" opacity={opacity(p.code)} className="cursor-pointer transition-opacity duration-300"
            onMouseEnter={() => onRegion?.(REGION_OF[p.code]?.id)} onClick={() => onCountry?.(p.code)} data-testid={`regions-pin-${p.code}`} />
        ))}
      </g>
    </svg>
  );
}
